import { useMedplumProfile } from '@medplum/react-hooks';
import type { Patient, Practitioner, RelatedPerson } from '@medplum/fhirtypes';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { EXTENSION_URLS } from '../features/rbac/constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PortalAccount {
  /** Patient id whose data should be loaded. */
  patientId: string;
  /** Display label, e.g. "My Portal — Emma Smith" or "Liam Jones". */
  label: string;
  /** True when this account is the authenticated user's own chart (not a related patient). */
  isSelf: boolean;
}

export interface PortalSubjectContextValue {
  /** All portal accounts the current user can view. Empty for non-client users. */
  availableAccounts: PortalAccount[];
  /** Currently selected portal account (null for non-client users or when unresolved). */
  activeAccount: PortalAccount | null;
  /** Active portal subject patient id (convenience shorthand). */
  activePatientId: string | undefined;
  /** Switch to a different portal account by patient id. */
  setActivePatientId: (patientId: string) => void;
  /** True when the user has more than one portal account (show switcher). */
  canSwitch: boolean;
}

const EMPTY: PortalSubjectContextValue = {
  availableAccounts: [],
  activeAccount: null,
  activePatientId: undefined,
  setActivePatientId: () => {},
  canSwitch: false,
};

const PortalSubjectContext = createContext<PortalSubjectContextValue>(EMPTY);

// ---------------------------------------------------------------------------
// Session persistence
// ---------------------------------------------------------------------------

const SESSION_KEY = 'unify.portalSubjectPatientId';

function getPersistedPatientId(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function setPersistedPatientId(id: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, id);
  } catch { /* noop */ }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPatientName(profile: Patient | Practitioner | RelatedPerson | undefined): string {
  if (!profile) return 'Unknown';
  const n = profile.name?.[0];
  if (n?.text) return n.text;
  const given = n?.given?.join(' ') ?? '';
  const family = n?.family ?? '';
  return `${given} ${family}`.trim() || 'Unknown';
}

function getPortalSubjectPatientRef(profile: Patient | undefined): string | undefined {
  const ext = profile?.extension?.find((e) => e.url === EXTENSION_URLS.portalSubjectPatient);
  const ref = ext?.valueReference?.reference;
  if (ref?.startsWith('Patient/')) {
    return ref.slice('Patient/'.length);
  }
  return undefined;
}

function getPortalRelatedPatients(profile: Patient | undefined): PortalAccount[] {
  if (!profile?.extension) return [];
  return profile.extension
    .filter((e) => e.url === EXTENSION_URLS.portalRelatedPatient && e.valueReference?.reference?.startsWith('Patient/'))
    .map((e) => ({
      patientId: e.valueReference!.reference!.slice('Patient/'.length),
      label: e.valueReference!.display || 'Linked Patient',
      isSelf: false,
    }));
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PortalSubjectProvider({ children }: { children: ReactNode }): JSX.Element {
  const profile = useMedplumProfile() as Patient | Practitioner | RelatedPerson | undefined;

  const availableAccounts = useMemo<PortalAccount[]>(() => {
    if (!profile) return [];

    // TODO: For RelatedPerson with multiple patients, enumerate from
    // ProjectMembership.access[].parameter entries. See PRD_NON_CLIENT_RELATED_PERSONS_FHIR_MAPPING.md
    if (profile.resourceType === 'RelatedPerson') {
      const accounts: PortalAccount[] = [];
      const relatedPerson = profile as RelatedPerson;
      const patientRef = relatedPerson.patient?.reference;
      const patientDisplay = relatedPerson.patient?.display ?? 'Patient';
      if (patientRef) {
        accounts.push({
          patientId: patientRef.replace('Patient/', ''),
          label: patientDisplay,
          isSelf: false,
        });
      }
      return accounts;
    }

    if (profile.resourceType !== 'Patient') {
      return [];
    }

    const selfId = profile.id;
    const portalSubjectId = getPortalSubjectPatientRef(profile);

    if (portalSubjectId && selfId && portalSubjectId !== selfId) {
      // Guarantor-only proxy: the profile is a login-only Patient, portal-subject is the real patient
      const subjectDisplay = profile.extension
        ?.find((e) => e.url === EXTENSION_URLS.portalSubjectPatient)
        ?.valueReference?.display;

      return [{
        patientId: portalSubjectId,
        label: subjectDisplay || 'Linked Patient',
        isSelf: false,
      }];
    }

    if (selfId) {
      const selfAccount: PortalAccount = {
        patientId: selfId,
        label: `My Portal — ${getPatientName(profile)}`,
        isSelf: true,
      };
      const relatedAccounts = getPortalRelatedPatients(profile);
      return [selfAccount, ...relatedAccounts];
    }

    return [];
  }, [profile]);

  const defaultPatientId = availableAccounts[0]?.patientId;

  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(() => {
    const persisted = getPersistedPatientId();
    return persisted;
  });

  const activePatientId = useMemo(() => {
    if (selectedPatientId && availableAccounts.some((a) => a.patientId === selectedPatientId)) {
      return selectedPatientId;
    }
    return defaultPatientId;
  }, [selectedPatientId, availableAccounts, defaultPatientId]);

  const activeAccount = useMemo(
    () => availableAccounts.find((a) => a.patientId === activePatientId) ?? null,
    [availableAccounts, activePatientId],
  );

  const setActivePatientId = useCallback((patientId: string) => {
    setSelectedPatientId(patientId);
    setPersistedPatientId(patientId);
  }, []);

  const canSwitch = availableAccounts.length > 1;

  const value = useMemo<PortalSubjectContextValue>(() => ({
    availableAccounts,
    activeAccount,
    activePatientId,
    setActivePatientId,
    canSwitch,
  }), [availableAccounts, activeAccount, activePatientId, setActivePatientId, canSwitch]);

  return (
    <PortalSubjectContext.Provider value={value}>
      {children}
    </PortalSubjectContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePortalSubject(): PortalSubjectContextValue {
  return useContext(PortalSubjectContext);
}
